class AppConfig {
  /**
   * Determines if application is run from a Web or from a NodeJs environment.
   * @returns {boolean}
   */
  static get IS_BROWSER() {
    return Object.prototype.toString.call(global.process) !== '[object process]';
  }

  static get API_ENDPOINT() {
    return process.env.API_ENDPOINT || 'http://localhost';
  }

  /**
   * @returns {string}
   */
  static get HOST_NAME() {
    const hostName = `${this.API_ENDPOINT}:9990`;
    // url, where search service is served, it has to be configured in proxy server (i.e. nginx)
    return AppConfig.IS_BROWSER ? '' : hostName;
  }

  /**
   * @returns {string}
   */
  static get WALLABY_CORE_SERVICE_URL() {
    return `${AppConfig.HOST_NAME}/wallabyCoreApi`;
  }

  /**
   * @returns {string}
   */
  static get HAIDI_SERVICE_URL() {
    return `${AppConfig.HOST_NAME}/haidiApi`;
  }

  /**
   * @returns {string}
   */
  static get KEYCLOAK_SERVICE_URL() {
    return `${AppConfig.HOST_NAME}/token`;
  }

  /**
   * @returns {string}
   */
  static withoutHostName(endpoint) {
    return endpoint.indexOf(AppConfig.HOST_NAME) === 0 ? endpoint.substring(AppConfig.HOST_NAME.length) : endpoint;
  }

  static getConfig() {
    return {
      class: AppConfig,
      service: {
        url: {
          // general
          configDataSet: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config-dataset`,
          coreEnums: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/enums`,

          // # WALLABY CORE
          appIndexFile: '/',
          autoLayout: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/miniscreens`,
          autoLayoutMetadataMethod: 'metadata',
          baseDashboardReportName: 'dashboard',
          breadcrumbObjectPath: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/navigation/objectPath`,
          cases: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/cases`,
          caseDocuments: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/files/all-by-case-id`,
          caseDocumentsPaginated: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/files/by-case-id`,
          caseDocumentsTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/case/documentstable`,
          caseProjectPropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/case/projectproperties`,
          casePropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/case/properties`,
          caseResultTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/case/searchresultstable`,
          caseTimelineMethod: 'timeline',
          caseTimelineTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/case/timelinetable`,
          caseTypes: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/case-types`,
          caseHierarchyNode: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/search/findByCaseObjectId`,
          coreTranslations: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/translations`,
          dashboard: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/homepage`,
          dashboardConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/wombat/homepage`,
          documentConceptsConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/concepts`,
          documentFiltersConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/filters`,
          documentHierarchiesTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/hierarchiestable`,
          documentLinkedFilesTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/linkedfilestable`,
          documentOtherFormatsTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/otherformatstable`,
          documentPreviewReportId: 'preview',
          documentProjectPropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/projectproperties`,
          documentPropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/properties`,
          documentReportsConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/reports`,
          documentReportsMethod: 'reports',
          documentResultTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/document/searchresultstable`,
          fileAnnotationsMethod: 'annotations',
          fileAttachedFilesMethod: 'attachedFiles',
          fileDownloadMethod: 'download-by-blobid',
          filePdfMethod: 'pdf',
          fileTextChunksMethod: 'textchunks',
          fileTextMethod: 'text',
          fileTypeMethod: 'type',
          files: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/files`,
          filesExportMethod: 'export',
          hais: `${AppConfig.HAIDI_SERVICE_URL}/api/encounter/{encounterId}/hai`,
          haisUpdate: `${AppConfig.HAIDI_SERVICE_URL}/api/encounter/{encounterId}/hai/{haiId}`,
          haisDelete: `${AppConfig.HAIDI_SERVICE_URL}/api/encounter/{encounterId}/hai/{haiId}`,
          haiOnsetDepartment: `${AppConfig.HAIDI_SERVICE_URL}/api/encounter/{encounterId}/hai/onset-department`,
          hierarchies: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies`,
          hierarchiesByPathsMethod: 'by-paths',
          hierarchiesByPrefixMethod: 'by-prefix',
          hierarchiesByIdsMethod: 'by-ids',
          hierarchy: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchy`,
          hierarchyChildren: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/search/findByTypeIdAndParentId`,
          hierarchyChildrenByParent: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/search/findByParentId`,
          hierarchyChildrenByType: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/search/findByTypeId`,
          hierarchyFilter: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/files/search/findByParentHierarchy`,
          hierarchyTypes: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchy-types`,
          i18nConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/i18n`,
          i18nLocaleMessages: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/i18n`,
          infections: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/infections`,
          infectionsFiltersConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/hai/filters`,
          infectionsPropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/hai/properties`,
          infectionsResultTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/hai/searchresultstable`,
          info: `${AppConfig.WALLABY_CORE_SERVICE_URL}/actuator`,
          instanceConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/instance`,
          keycloak: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/keycloak`,
          snomedLabels: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/resources/snomed-labels`,
          processingStatus: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/application-processing-status`,

          // login
          login: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/login`,
          logout: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/logout`,

          miniScreensConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/miniscreens`,
          navigationConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/navigation`,

          // organization
          organizations: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/organizations/all`,
          organizationsFilterData: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/organizations/filter-data`,
          organizationsFilterDataByOrgSecurity: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/organizations/filter-data-by-org-security`,

          // medications
          medications: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/resources/medications`,
          medicationAtcGroups: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/resources/medications/{id}/atc-hierarchy-groups`,
          medicationAtcGroupsByMedicationId: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/resources/medications/{id}/atc-hierarchy-groups`,
          medicationsFilterData: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/atc-hierarchy/filter-data`,

          // patient
          patientsRoot: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/patients`,
          patients: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/patients/page`,
          patientsFiltersConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/patient/filters`,
          patientsPropertiesConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/patient/properties`,
          patientsResultTableConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/patient/searchresultstable`,

          // encounters
          documents: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/documents/encounter/`,
          chartRoot: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/lab-results`,
          chart: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/lab-results/encounter/`,

          projectConfig: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/config/project`,
          propertyTypes: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/property-types`,
          range: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/range`,
          search: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/search`,
          security: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/security`,
          securedHierarchiesByPathsMethod: 'secured-by-paths',
          securedHierarchiesByPrefixMethod: 'secured-by-prefix',
          securedHierarchyChildrenByParent: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/securedFindByParentId`,
          securedHierarchyChildrenByType: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/hierarchies/securedFindByTypeId`,
          token: `${AppConfig.KEYCLOAK_SERVICE_URL}`,
          translate: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/translate/translate`,
          translateIssueToken: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/translate/issueToken`,
          upload: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/files`,
          userFilters: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/user-filters`,

          // reports
          reports: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/reports`,

          // # HAIDI
          // general
          permissions: `${AppConfig.HAIDI_SERVICE_URL}/api/permissions`,
          haidiEnums: `${AppConfig.HAIDI_SERVICE_URL}/api/enums`,
          haidiTranslations: `${AppConfig.HAIDI_SERVICE_URL}/api/translations`,
          maxExportRows: `${AppConfig.HAIDI_SERVICE_URL}/api/instance/max-export-rows`,

          // pathogens
          pathogens: `${AppConfig.HAIDI_SERVICE_URL}/api/hai-pathogens/all`,

          // hai
          haiDashboardService: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/dashboard`,
          haiList: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/discovery/page`,
          haiListXlsxExport: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/discovery/xlsx-export`,

          // reports
          singleDepartmentReport: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/report/single-department`,
          multipleDepartmentReport: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/report/multiple-departments`,
          departmentReport: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/report/department-report`,
          departmentReportPdfExport: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/report/department-report/pdf-export`,
          pathogenReport: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/report/pathogen-report`,
          haiYearlyOverviewReport: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/report/yearly-overview-report`,
          haiXlsxExport: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/report/{reportName}/xlsx-export`,

          // atb-stewardship
          antibiogram: `${AppConfig.HAIDI_SERVICE_URL}/api/atb-stewardship/antibiogram`,
          labsFinding: `${AppConfig.HAIDI_SERVICE_URL}/api/atb-stewardship/lab-result-finding-list`,
          labsAtbList: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/lab-results/atb-list`,
          labsList: `${AppConfig.HAIDI_SERVICE_URL}/api/atb-stewardship/lab-result-page`,
          labsListXlsxExport: `${AppConfig.HAIDI_SERVICE_URL}/api/atb-stewardship/lab-result-page/xlsx-export`,
          pathogenDetail: `${AppConfig.HAIDI_SERVICE_URL}/api/atb-stewardship/pathogen-detail`,

          // encounter
          encounter: `${AppConfig.HAIDI_SERVICE_URL}/api/encounter/`,
          encounterDocument: `${AppConfig.HAIDI_SERVICE_URL}/api/encounter/{encounterId}/document-version/{documentId}`,
          encounterProperties: `${AppConfig.HAIDI_SERVICE_URL}/api/encounter/{encounterId}/properties`,

          // document
          document: `${AppConfig.HAIDI_SERVICE_URL}/api/document-version/{documentId}`,

          // patients
          haiPatients: `${AppConfig.HAIDI_SERVICE_URL}/api/patients/`,
          patientDetail: `${AppConfig.HAIDI_SERVICE_URL}/api/patient/{patientId}`,

          // saved filters
          userFilterDelete: `${AppConfig.HAIDI_SERVICE_URL}/api/user-filter/{userFilterId}`,
          userFilterGet: `${AppConfig.HAIDI_SERVICE_URL}/api/user-filter/all`,
          userFilterPost: `${AppConfig.HAIDI_SERVICE_URL}/api/user-filter`,
          defaultUserFilterPost: `${AppConfig.HAIDI_SERVICE_URL}/api/user-filter/component/{componentName}/{userFilterId}?default={default}`,
          userFilterShare: `${AppConfig.HAIDI_SERVICE_URL}/api/user-filter/{userFilterId}/share`,
          userFilterRevertToPrivate: `${AppConfig.HAIDI_SERVICE_URL}/api/user-filter/{userFilterId}/revert-to-private`,
          userFilterDeleteShared: `${AppConfig.HAIDI_SERVICE_URL}/api/user-filter/shared/{userFilterId}`,

          // standard types
          standardType: `${AppConfig.HAIDI_SERVICE_URL}/api/instance/hai-standard-type`,
          standardTypeList: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/resources/hai-standard-type`,
          standardTypeTranslations: `${AppConfig.WALLABY_CORE_SERVICE_URL}/api/resources/hai-standard-type-translation`,

          // department dropdown grouping (org-security preferred-first divider)
          haidiGroupDeptsBySecurity: `${AppConfig.HAIDI_SERVICE_URL}/api/instance/group-depts-by-security`,

          // invasive devices - value-list autocomplete/filter values (merged predefined + user-defined)
          invasiveDeviceValues: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/invasive-devices/values`,

          // surgery types (SSI) - value-list autocomplete/filter values
          surgeryTypeValues: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/surgery-types/values`,

          // user-defined tags (all HAI types) - value-list autocomplete/filter values
          userTagValues: `${AppConfig.HAIDI_SERVICE_URL}/api/hai/user-tags/values`,
        },
      },
    };
  }
}

export default AppConfig.getConfig();
